<template>
  <DModal :isBgOverlay="true" :showClose="false" :placeInTop="true" @close="$emit('close')">
    <div class="employment-rates" slot="insertComponent">
      <div class="employment-rates__header">
        <h2>Ansættelsesgrader<span v-if="schoolYearName"> {{ schoolYearName }}</span></h2>
        <div class="icon__bg-circle doc-modal__close" @click="$emit('close')">
          <span class="icon-cross2"></span>
        </div>
      </div>
      <div class="employment-rates__body">
        <div class="do-spinner__container" v-if="isLoading">
          <div class="do-spinner do-spinner--large"></div>
        </div>
        <table class="do-table" v-else>
          <thead>
          <tr>
            <th @click="setSort('name')" class="is-clickable">
              Navn
              <span v-if="sort.key === 'name'">{{ sort.dir === 'asc' ? '▲' : '▼' }}</span>
            </th>
            <th
              v-for="col in columns"
              :key="'h-' + col"
              @click="setSort(col)"
              class="is-clickable"
            >
              {{ columnLabel(col) }}
              <span v-if="sort.key === col">{{ sort.dir === 'asc' ? '▲' : '▼' }}</span>
            </th>
          </tr>
          </thead>
          <tbody>
          <tr v-for="row in displayedRows" :key="row.id">
            <td class="bold">{{ row.name }}</td>
            <td v-for="col in columns" :key="'c-' + row.id + '-' + col">
              {{ row.values[col] }}
            </td>
          </tr>
          <tr v-if="!displayedRows.length">
            <td :colspan="columns.length + 1" class="employment-rates__empty">
              Ingen data for det valgte skoleår
            </td>
          </tr>
          </tbody>
        </table>
      </div>
      <div class="employment-rates__footer">
        <button class="do-bttn" @click="$emit('close')">Luk</button>
        <button class="do-bttn do-bttn--submit"
                :class="{'do-bttn--submit--loading': isDownloading}"
                :disabled="isLoading || !displayedRows.length"
                @click="asyncLoadXLSXAndDownload">
          Download til Excel
        </button>
      </div>
    </div>
  </DModal>
</template>

<script>
import {mapGetters} from 'vuex'
import DModal from '../common/DModal'
import docendo from '../../docendo'

let XLSX = null

export default {
  components: {DModal},
  data () {
    return {
      userYearsByUserId: {},
      isLoading: false,
      isDownloading: false,
      sort: {key: 'name', dir: 'asc'}
    }
  },
  computed: {
    ...mapGetters(['usersSortedOnName']),
    schoolId () {
      return this.$store.getters.schoolId
    },
    activeSchoolYear () {
      return this.$store.state.localActivatedSchoolYear
    },
    schoolYearName () {
      return this.activeSchoolYear && this.activeSchoolYear.name ? this.activeSchoolYear.name : ''
    },
    coreColumns () {
      return ['Ansættelsesgrad', 'Timeansættelse pr. uge']
    },
    columns () {
      const cols = {}
      this.coreColumns.forEach(c => { cols[c] = true })
      const users = this.usersSortedOnName || []
      for (let i = 0; i < users.length; i++) {
        const year = this.userYearsByUserId[users[i].id] || {}
        const norm = year && Array.isArray(year.norm) ? year.norm : []
        for (let j = 0; j < norm.length; j++) {
          const entry = norm[j]
          if (!entry || !entry.title) continue
          if (entry.title.indexOf('hidden_') === 0) continue
          cols[entry.title] = true
        }
      }
      const ordered = Object.keys(cols)

      // Placér 'Periode' (ansættelsesperioden) lige efter 'Bruttotid'
      const periodeIdx = ordered.indexOf('Periode')
      const bruttoIdx = ordered.indexOf('Bruttotid')
      if (periodeIdx > -1 && bruttoIdx > -1) {
        ordered.splice(periodeIdx, 1)
        ordered.splice(ordered.indexOf('Bruttotid') + 1, 0, 'Periode')
      }
      return ordered
    },
    rows () {
      const users = this.usersSortedOnName || []
      return users.map(u => {
        const year = this.userYearsByUserId[u.id] || {}
        const norm = year && Array.isArray(year.norm) ? year.norm : []
        const values = {}
        for (let i = 0; i < this.columns.length; i++) values[this.columns[i]] = ''
        for (let k = 0; k < norm.length; k++) {
          const e = norm[k]
          if (!e || !e.title) continue
          if (e.title.indexOf('hidden_') === 0) continue
          values[e.title] = e.value
        }
        return {
          id: u.id,
          name: u && u.name ? u.name : '',
          values: values
        }
      })
    },
    displayedRows () {
      const list = this.rows.slice()
      const key = this.sort.key
      const dir = this.sort.dir

      if (key === 'name') {
        list.sort((a, b) => {
          const cmp = String(a.name || '').localeCompare(String(b.name || ''), 'da-DK')
          return dir === 'asc' ? cmp : -cmp
        })
        return list
      }

      function toNum (val) {
        const s = String(val === undefined || val === null ? '' : val)
          .replace('%', '')
          .replace(',', '.')
        const n = parseFloat(s)
        return isNaN(n) ? null : n
      }

      list.sort((a, b) => {
        const avRaw = a.values[key]
        const bvRaw = b.values[key]
        const avNum = toNum(avRaw)
        const bvNum = toNum(bvRaw)
        if (avNum !== null && bvNum !== null) {
          return dir === 'asc' ? avNum - bvNum : bvNum - avNum
        }
        const at = String(avRaw === undefined || avRaw === null ? '' : avRaw)
        const bt = String(bvRaw === undefined || bvRaw === null ? '' : bvRaw)
        const cmp = at.localeCompare(bt, 'da-DK')
        return dir === 'asc' ? cmp : -cmp
      })
      return list
    }
  },
  methods: {
    columnLabel (col) {
      if (col === 'Periode') return 'Ansættelsesperiode'
      if (col === 'Arbejdstid') return 'Nettotid'
      return col
    },
    parseNumericValue (val) {
      if (val === null || val === undefined) return null
      const s = String(val).trim()
      // Kun rene tal (evt. med komma eller punktum som decimaltegn)
      if (!/^-?\d+(?:[.,]\d+)?$/.test(s)) return null
      const n = parseFloat(s.replace(',', '.'))
      return isNaN(n) ? null : n
    },
    setSort (key) {
      if (this.sort.key === key) {
        this.sort.dir = this.sort.dir === 'asc' ? 'desc' : 'asc'
        return
      }
      this.sort.key = key
      this.sort.dir = key === 'name' ? 'asc' : 'desc'
    },
    loadUserYears () {
      if (!this.activeSchoolYear || !this.activeSchoolYear.id) {
        this.userYearsByUserId = {}
        return
      }
      this.isLoading = true
      docendo.schoolUserYearsList(this.schoolId, this.activeSchoolYear.id, null, success => {
        const list = Array.isArray(success) ? success : []
        const byUserId = {}
        for (let i = 0; i < list.length; i++) {
          const userYear = list[i]
          if (userYear && userYear.user_id) {
            byUserId[userYear.user_id] = userYear
          }
        }
        this.userYearsByUserId = byUserId
        this.isLoading = false
      }, failure => {
        this.userYearsByUserId = {}
        this.isLoading = false
        console.log('schoolUserYearsList failed', failure)
      })
    },
    asyncLoadXLSXAndDownload () {
      this.isDownloading = true
      if (window.XLSX && XLSX) {
        this.download()
      } else {
        const cdnXLSX = document.createElement('script')
        cdnXLSX.setAttribute('src', 'https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.mini.min.js')
        document.head.appendChild(cdnXLSX)
        let intervalTimer = setInterval(_ => {
          if (window.XLSX) {
            clearInterval(intervalTimer)
            XLSX = window.XLSX
            this.download()
          }
        }, 500)
      }
    },
    download () {
      const rows = this.displayedRows.map(row => {
        const obj = {'Navn': row.name}
        this.columns.forEach(col => {
          const num = this.parseNumericValue(row.values[col])
          // Skriv tal som rigtige numeriske celler, så Excel kan summere kolonnen
          obj[this.columnLabel(col)] = num !== null ? num : (row.values[col] || '')
        })
        return obj
      })
      const headers = ['Navn'].concat(this.columns.map(col => this.columnLabel(col)))
      const data = XLSX.utils.json_to_sheet(rows, {header: headers})
      const wb = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(wb, data, 'Ansættelsesgrader')
      XLSX.writeFile(wb, 'docendo_ansaettelsesgrader.xlsx')
      this.isDownloading = false
    }
  },
  mounted () {
    this.$store.dispatch('getUsersFromSchoolId', this.schoolId)
    this.loadUserYears()
  }
}
</script>

<style scoped>
.employment-rates {
  background: #fff;
  border-radius: 4px;
  width: calc(100vw - 4rem);
}

.employment-rates__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem;
  border-bottom: 1px solid #dddddd;
}

.employment-rates__body {
  background: var(--bg-grey);
  padding: 1rem;
  max-height: calc(100vh - (52px + 53px + 4rem));
  overflow-y: auto;
  overscroll-behavior: contain;
}

.employment-rates__footer {
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  padding: 1rem;
  border-top: 1px solid #dddddd;
}

.is-clickable {
  cursor: pointer;
}

.employment-rates .do-table {
  width: 100%;
  background: #fff;
}

.employment-rates .do-table thead th {
  padding: 0.5rem;
  text-align: left;
  background: #f6f9fc;
  border-bottom: 1px solid #e2e8f0;
  white-space: nowrap;
}

.employment-rates .do-table tbody tr {
  border-bottom: 1px solid #e2e8f0;
  font-weight: normal;
}

.employment-rates .do-table tbody td {
  padding: 0.4rem 0.5rem;
}

.employment-rates .do-table tbody td:not(:last-child) {
  border-right: 1px solid #e2e8f0;
}

.employment-rates__empty {
  text-align: center;
  padding: 1.5rem !important;
  color: var(--grey);
}

.bold {
  font-weight: bold;
}

.do-spinner__container {
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 50px 0;
}
</style>



// WEBPACK FOOTER //
// AssignHoursEmploymentRates.vue?5ca81629